import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  DialogFormSection,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { eduIntegrationsAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { hasAnyRole } from '../lib/rbac';
import { Plug, PlugZap, Loader2, RefreshCw, Link2, Unlink, CheckCircle2, XCircle } from 'lucide-react';
import Swal from 'sweetalert2';
import { StatCard } from './common/StatCard';

// The value the API sends back in place of a stored secret. Echoing it on save
// means "keep the key you already have"; the real key never reaches the browser.
const SECRET_MASK = '********';

// Learniv is configured through named fields rather than a JSON blob: it is the
// one connector with a real adapter behind it, so the form can ask for exactly
// what the adapter needs and nothing else.
const LEARNIV_FIELDS = [
  {
    key: 'website_url',
    label: 'Website',
    placeholder: 'https://learniv.in',
    hint: 'Public site. Shown for reference; not called by the sync.',
  },
  {
    key: 'lms_url',
    label: 'LMS URL',
    placeholder: 'https://lms.learniv.in',
    hint: 'Where learners sign in. Used to build course links.',
  },
  {
    key: 'api_base_url',
    label: 'API base URL',
    placeholder: 'https://api.learniv.in',
    hint: 'The API host, not the LMS. Requests go to /api/v1/external.',
  },
  {
    key: 'api_key',
    label: 'API key',
    placeholder: 'sk_live_…',
    secret: true,
    hint: 'Needs the courses:read and users:read scopes. Stored server-side and never shown again.',
  },
];

const LEARNIV_DEFAULTS = {
  website_url: 'https://learniv.in',
  lms_url: 'https://lms.learniv.in',
  api_base_url: 'https://api.learniv.in',
  api_key: '',
};

// Known connectors surfaced as cards, grouped loosely by category for the label copy.
const PROVIDERS = [
  { id: 'learniv', label: 'Learniv', category: 'LMS', fields: LEARNIV_FIELDS, defaults: LEARNIV_DEFAULTS },
  { id: 'moodle', label: 'Moodle', category: 'LMS' },
  { id: 'canvas', label: 'Canvas', category: 'LMS' },
  { id: 'google_classroom', label: 'Google Classroom', category: 'LMS' },
  { id: 'sis', label: 'Student Information System', category: 'SIS' },
  { id: 'm365', label: 'Microsoft 365', category: 'Identity' },
  { id: 'google_workspace', label: 'Google Workspace', category: 'Identity' },
  { id: 'sso_saml', label: 'SSO (SAML)', category: 'SSO' },
  { id: 'sso_oauth', label: 'SSO (OAuth)', category: 'SSO' },
];

const STATUS_BADGE = {
  connected: { variant: 'secondary', label: 'Connected', Icon: CheckCircle2, cls: 'text-emerald-600' },
  disconnected: { variant: 'outline', label: 'Disconnected', Icon: XCircle, cls: 'text-muted-foreground' },
  error: { variant: 'destructive', label: 'Error', Icon: XCircle, cls: 'text-rose-600' },
};

const toArray = (res) => {
  const d = res?.data;
  if (Array.isArray(d)) return d;
  return d?.items || d?.results || [];
};

const EducationIntegrationsManagement = () => {
  const { user } = useAuth();
  const canManage = Boolean(user?.isSuperadmin) || hasAnyRole(user?.role, ['admin', 'workspace_admin', 'hr_admin']);

  const [loading, setLoading] = useState(false);
  const [connections, setConnections] = useState([]);
  const [busy, setBusy] = useState(null); // provider id currently syncing/disconnecting

  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeProvider, setActiveProvider] = useState(null);
  const [displayName, setDisplayName] = useState('');
  const [configText, setConfigText] = useState('');
  const [fieldValues, setFieldValues] = useState({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const res = await eduIntegrationsAPI.list();
      setConnections(toArray(res));
    } catch (err) {
      // 402 → add-on not active; surface a clear, non-alarming message.
      if (err?.response?.status === 402) {
        setConnections([]);
      } else {
        Swal.fire('Could not load integrations', err?.response?.data?.detail || 'Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadConnections(); }, []);

  const byProvider = useMemo(() => {
    const map = {};
    connections.forEach((c) => { map[c.provider] = c; });
    return map;
  }, [connections]);

  const stats = useMemo(() => {
    const connected = connections.filter((c) => c.status === 'connected').length;
    const errored = connections.filter((c) => c.status === 'error').length;
    return { total: PROVIDERS.length, connected, errored };
  }, [connections]);

  const openConnect = (provider) => {
    const existing = byProvider[provider.id];
    setActiveProvider(provider);
    setDisplayName(existing?.display_name || provider.label);
    setTestResult(null);
    if (provider.fields) {
      // Stored values win, then the provider's own hosts, so a first-time
      // connect only has to supply the key.
      const stored = existing?.config || {};
      setFieldValues(
        provider.fields.reduce((acc, field) => {
          acc[field.key] = stored[field.key] ?? provider.defaults?.[field.key] ?? '';
          return acc;
        }, {}),
      );
    } else {
      setConfigText(existing?.config ? JSON.stringify(existing.config, null, 2) : '');
    }
    setDialogOpen(true);
  };

  // The config the dialog would submit, in whichever shape this provider uses.
  const buildConfig = () => {
    if (activeProvider?.fields) {
      return activeProvider.fields.reduce((acc, field) => {
        const value = String(fieldValues[field.key] ?? '').trim();
        // An untouched secret field still holds the mask; sending it back tells
        // the server to keep the stored key. An empty one is simply omitted.
        if (value) acc[field.key] = value;
        return acc;
      }, {});
    }
    const raw = configText.trim();
    if (!raw) return undefined;
    return JSON.parse(raw); // caller handles the throw
  };

  const testConnection = async () => {
    if (!activeProvider) return;
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await eduIntegrationsAPI.test(activeProvider.id, { config: buildConfig() });
      setTestResult({ ok: true, ...data });
    } catch (err) {
      setTestResult({
        ok: false,
        message: err?.response?.data?.detail || 'Could not reach the provider.',
      });
    } finally {
      setTesting(false);
    }
  };

  const submitConnect = async () => {
    if (!activeProvider) return;
    let config;
    try {
      config = buildConfig();
    } catch (e) {
      return Swal.fire('Invalid config', 'Config must be valid JSON (or left blank).', 'warning');
    }
    try {
      await eduIntegrationsAPI.connect(activeProvider.id, {
        display_name: displayName?.trim() || activeProvider.label,
        config,
      });
      setDialogOpen(false);
      await loadConnections();
      Swal.fire({ icon: 'success', title: 'Connected', timer: 1300, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Connect failed', err?.response?.data?.detail || 'Please review the config and try again.', 'error');
    }
  };

  const sync = async (provider) => {
    setBusy(provider.id);
    try {
      const res = await eduIntegrationsAPI.sync(provider.id);
      await loadConnections();
      Swal.fire({
        icon: 'success',
        title: 'Sync triggered',
        text: res?.data?.note || 'Sync completed.',
        timer: 1800,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire('Sync failed', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setBusy(null);
    }
  };

  const disconnect = async (provider) => {
    const ok = await Swal.fire({
      icon: 'warning', title: 'Disconnect integration?',
      text: `${provider.label} will be disconnected and its stored configuration removed.`,
      showCancelButton: true, confirmButtonText: 'Disconnect', confirmButtonColor: '#b5456b',
    });
    if (!ok.isConfirmed) return;
    setBusy(provider.id);
    try {
      await eduIntegrationsAPI.remove(provider.id);
      await loadConnections();
      Swal.fire({ icon: 'success', title: 'Disconnected', timer: 1200, showConfirmButton: false });
    } catch (err) {
      Swal.fire('Disconnect failed', err?.response?.data?.detail || 'Please try again.', 'error');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-semibold flex items-center gap-2">
            <Plug className="h-6 w-6 text-indigo-600" /> Integrations
          </h2>
          <p className="text-sm text-muted-foreground">
            Connect your LMS, Student Information System, identity suites and SSO providers to ViteHR.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard label="Available Connectors" value={stats.total} icon={Plug} />
        <StatCard label="Connected" value={stats.connected} icon={CheckCircle2} />
        <StatCard label="Errored" value={stats.errored} icon={XCircle} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Connectors</CardTitle>
          <CardDescription>LMS, SIS, identity and SSO providers for this institute.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading integrations…
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {PROVIDERS.map((provider) => {
                const conn = byProvider[provider.id];
                const status = conn?.status || 'disconnected';
                const meta = STATUS_BADGE[status] || STATUS_BADGE.disconnected;
                const isBusy = busy === provider.id;
                const isConnected = status === 'connected' || status === 'error';
                return (
                  <div key={provider.id} className="rounded-lg border p-4 flex flex-col gap-3">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="font-medium flex items-center gap-2">
                          <meta.Icon className={`h-4 w-4 ${meta.cls}`} />
                          {conn?.display_name || provider.label}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">{provider.category}</div>
                      </div>
                      <Badge variant={meta.variant}>{meta.label}</Badge>
                    </div>
                    {conn?.last_sync_at && (
                      <div className="text-xs text-muted-foreground">
                        Last synced: {new Date(conn.last_sync_at).toLocaleString()}
                      </div>
                    )}
                    {canManage && (
                      <div className="flex flex-wrap gap-2 mt-auto pt-1">
                        <Button size="sm" variant={isConnected ? 'outline' : 'default'} onClick={() => openConnect(provider)}>
                          <Link2 className="h-4 w-4 mr-1" /> {isConnected ? 'Reconfigure' : 'Connect'}
                        </Button>
                        {isConnected && (
                          <Button size="sm" variant="outline" disabled={isBusy} onClick={() => sync(provider)}>
                            {isBusy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Sync
                          </Button>
                        )}
                        {isConnected && (
                          <Button size="sm" variant="ghost" disabled={isBusy} onClick={() => disconnect(provider)}>
                            <Unlink className="h-4 w-4 mr-1 text-rose-600" /> Disconnect
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Connect / reconfigure dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{activeProvider ? `Connect ${activeProvider.label}` : 'Connect'}</DialogTitle>
          </DialogHeader>
          <DialogFormSection>
            <div className="space-y-1">
              <Label>Display Name</Label>
              <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder={activeProvider?.label} />
            </div>

            {activeProvider?.fields ? (
              activeProvider.fields.map((field) => (
                <div className="space-y-1" key={field.key}>
                  <Label htmlFor={`cfg-${field.key}`}>{field.label}</Label>
                  <Input
                    id={`cfg-${field.key}`}
                    type={field.secret ? 'password' : 'text'}
                    autoComplete={field.secret ? 'new-password' : 'off'}
                    value={fieldValues[field.key] ?? ''}
                    placeholder={field.placeholder}
                    onChange={(e) => {
                      setTestResult(null);
                      setFieldValues((prev) => ({ ...prev, [field.key]: e.target.value }));
                    }}
                    onFocus={(e) => {
                      // Clear the mask on focus so typing replaces the stored key
                      // instead of appending to a row of asterisks.
                      if (field.secret && e.target.value === SECRET_MASK) {
                        setFieldValues((prev) => ({ ...prev, [field.key]: '' }));
                      }
                    }}
                  />
                  {field.hint && <p className="text-xs text-muted-foreground">{field.hint}</p>}
                </div>
              ))
            ) : (
              <div className="space-y-1">
                <Label>Configuration (JSON)</Label>
                <textarea
                  className="w-full min-h-[140px] rounded-md border border-input bg-background px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                  value={configText}
                  onChange={(e) => setConfigText(e.target.value)}
                  placeholder={'{\n  "base_url": "https://lms.example.edu",\n  "client_id": "…"\n}'}
                />
                <p className="text-xs text-muted-foreground">
                  Optional. Provide connection details as JSON. Secrets are stored securely and never displayed back.
                </p>
              </div>
            )}

            {testResult && (
              <div
                className={`rounded-md border p-3 text-sm ${
                  testResult.ok
                    ? 'border-emerald-200 bg-emerald-50/60 text-emerald-900'
                    : 'border-rose-200 bg-rose-50/60 text-rose-900'
                }`}
              >
                {testResult.ok ? (
                  <>
                    <div className="font-medium">Connected to {testResult.api_base_url}</div>
                    <div className="mt-1 text-xs">
                      Workspace {testResult.lms_tenant_id || '—'} · scopes:{' '}
                      {testResult.scopes?.length ? testResult.scopes.join(', ') : 'none granted'}
                    </div>
                  </>
                ) : (
                  <div>{testResult.message}</div>
                )}
              </div>
            )}
          </DialogFormSection>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            {activeProvider?.fields && (
              <Button variant="outline" onClick={testConnection} disabled={testing}>
                {testing ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <PlugZap className="h-4 w-4 mr-1" />}
                Test connection
              </Button>
            )}
            <Button onClick={submitConnect}>Connect</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EducationIntegrationsManagement;
